import ReportProperty from '@/Enums/Report/BeeswaxReportProperties.enum';
import type { IState } from '@/Interfaces/Slices/ReportingDashboard/Google/State.interface';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { fulfilledReportCaseHandler } from '@/helpers/reportingDashboard/slice';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { boundaryMap, getGoogleCampaignData, getGoogleDateData, reportMap } from './google.thunks';

const initialState: IState = {
  dateData: [],
  channelData: [],
  deviceData: [],
  campaignData: [],
  advertiserData: [],
  placementData: [],
  creativeData: [],
  publisherData: [],
  daysOfWeekData: [],
  daypartData: [],
  stateData: [],
  dmaData: [],
  appUrlReport: [],

  campaigns: [],

  stateBoundariesData: null,
  dmaBoundariesData: null,

  totals: [],
  totalsRequestId: DEFAULT_LOADING_REQUEST_ID,

  dateDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  channelDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  deviceDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  campaignDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  advertiserDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  placementDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  creativeDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  publisherDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  daysOfWeekDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  daypartDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  stateDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  dmaDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  appUrlReportRequestId: DEFAULT_LOADING_REQUEST_ID,

  stateBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  dmaBoundariesDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  errorCardMessage: '',
};

const reportRequestIdVariables: (keyof IState)[] = [
  'dateDataRequestId',
  'appUrlReportRequestId',
  'advertiserDataRequestId',
  'channelDataRequestId',
  'deviceDataRequestId',
  'campaignDataRequestId',
  'placementDataRequestId',
  'creativeDataRequestId',
  'publisherDataRequestId',
  'daysOfWeekDataRequestId',
  'daypartDataRequestId',
  'stateDataRequestId',
  'dmaDataRequestId',
  'totalsRequestId',
];

export const reportingDashboardGoogle = createSlice({
  name: 'reportingDashboardGoogle',
  initialState,
  reducers: {
    resetGoogleLoadingState: (state, action?: PayloadAction) => {
      const newState = action?.payload ?? DEFAULT_LOADING_REQUEST_ID;
      reportRequestIdVariables.forEach(key => {
        (state[key as keyof IState] as string) = newState;
      });
      state.errorCardMessage = '';
    },
  },
  extraReducers: builder => {
    reportMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, pendingCaseHandler(key, []))
        .addCase(thunk.rejected, rejectedCaseHandler(key, []))
        .addCase(thunk.fulfilled, fulfilledReportCaseHandler(key));
    });

    boundaryMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, pendingCaseHandler(key))
        .addCase(thunk.rejected, rejectedCaseHandler(key))
        .addCase(thunk.fulfilled, fulfilledCaseHandler(key));
    });

    builder
      //#region date report
      .addCase(getGoogleDateData.pending, pendingCaseHandler('dateData', []))
      .addCase(getGoogleDateData.rejected, (state, action) => {
        if (state.dateDataRequestId === action.meta.requestId) {
          state.dateDataRequestId = null;
          state.errorCardMessage = 'There seems to be a momentary issue loading your data. Please retry.';
        }
      })
      .addCase(getGoogleDateData.fulfilled, (state, action) => {
        if (state.dateDataRequestId === action.meta.requestId) {
          state.errorCardMessage =
            action.payload.errorCardMessage ??
            (!action.payload.report?.length
              ? "We couldn't find any data. Please adjust your filters and try again."
              : '');
          state.dateDataRequestId = null;
          state.dateData = action.payload?.report;
        }
      })
      //#endregion

      .addCase(getGoogleCampaignData.pending, pendingCaseHandler('campaignData', []))
      .addCase(getGoogleCampaignData.rejected, rejectedCaseHandler('campaignData', []))
      .addCase(getGoogleCampaignData.fulfilled, (state, action) => {
        const { filters, report } = action.payload ?? {};
        const dataVariableName = 'campaignData';
        const requestIdVariableName = `${dataVariableName}RequestId`;
        if (state[requestIdVariableName] === action.meta.requestId) {
          state[requestIdVariableName] = null;
          state[dataVariableName] = report;
          if (filters?.campaigns?.length === 0) {
            state.campaigns = report
              ?.map(campaign => ({
                value: campaign[ReportProperty.CAMPAIGN_ID],
                label: campaign[ReportProperty.CAMPAIGN_NAME],
              }))
              .sort((a, b) => a.label?.localeCompare(b?.label));
          }
        }
      });
  },
});

export const { resetGoogleLoadingState } = reportingDashboardGoogle.actions;

export default reportingDashboardGoogle.reducer;
